<script lang="ts">
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import LanguageSwitcher from "$components/LanguageSwitcher.svelte";
  import AuthHero from "./AuthHero.svelte";
  import { get } from "svelte/store";
  import { auth, login } from "$lib/auth";
  import { homePath } from "$lib/surfaces";
  import { ApiError } from "$lib/api";
  import { setPendingVerificationEmail } from "$lib/verifyEmail";

  let email = $state("");
  let password = $state("");
  let showPassword = $state(false);
  let remember = $state(true);
  let error = $state<string | null>(null);
  let notice = $state<string | null>(null);
  let loading = $state(false);

  function readNotice(): string | null {
    const query = window.location.hash.split("?")[1] ?? "";
    const reason = new URLSearchParams(query).get("reason");
    if (reason === "session-expired") return $_("auth.sessionExpired");
    if (reason === "password-reset") return $_("auth.passwordResetDone");
    if (reason === "invite-accepted") return $_("auth.inviteAcceptedNotice");
    return null;
  }

  onMount(() => {
    notice = readNotice();
    const onHashChange = () => {
      notice = readNotice();
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  });

  async function onSubmit(e: Event) {
    e.preventDefault();
    error = null;
    notice = null;
    loading = true;
    try {
      const data = await login(email, password, remember);
      const slug = data.memberships[0]?.tenant.slug;
      if (slug) {
        // Users who never finished onboarding resume it on login.
        if (!data.user?.onboarding_completed_at) {
          push(`/t/${slug}/onboarding`);
        } else {
          push(homePath(get(auth), slug));
        }
      } else if (data.user?.is_staff) {
        // Platform operators have no company of their own; the console is
        // their home screen. Without this they landed on "/no-tenant", which
        // fell through the router's "*" back onto this very login form.
        push("/backoffice");
      } else {
        push("/no-tenant");
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "EMAIL_NOT_VERIFIED") {
        // Right password, unfinished signup. The backend has already mailed a
        // fresh code, so drop straight onto the code wall.
        setPendingVerificationEmail(email.trim());
        push("/verify-email");
      } else if (err instanceof ApiError) {
        error = err.message;
      } else {
        error = $_("auth.unexpectedError");
      }
    } finally {
      loading = false;
    }
  }
</script>

<div class="page">
  <AuthHero />

  <section class="form">
    <div class="form-inner">
    <div class="lang-row">
      <a href="#/login" class="mobile-brand" aria-label="Order Evolution"><img src="/logo.svg" alt="Order Evolution" /></a>
      <LanguageSwitcher />
    </div>
    <form onsubmit={onSubmit} class="card login-card">
      <h2>{$_("auth.welcome")}</h2>
      <p class="muted">{$_("auth.welcome.sub")}</p>

      {#if notice}
        <div class="notice" role="status">
          <span class="material-icons-outlined" aria-hidden="true">info</span>
          <span>{notice}</span>
        </div>
      {/if}

      <div class="field">
        <label for="email">{$_("auth.email")}</label>
        <input id="email" type="email" autocomplete="email" bind:value={email} placeholder={$_("auth.email.placeholder")} required />
      </div>

      <div class="field">
        <label for="password">{$_("auth.password")}</label>
        <div class="pw-wrap">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            autocomplete="current-password"
            bind:value={password}
            placeholder={$_("auth.password.placeholder")}
            required
          />
          <button
            type="button"
            class="pw-toggle"
            aria-label={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
            aria-pressed={showPassword}
            title={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
            onclick={() => (showPassword = !showPassword)}
          >
            <span class="material-icons-outlined" aria-hidden="true">
              {showPassword ? "visibility_off" : "visibility"}
            </span>
          </button>
        </div>
      </div>

      <div class="row">
        <label class="checkbox">
          <input type="checkbox" bind:checked={remember} /> {$_("auth.remember")}
        </label>
        <span class="spacer"></span>
        <a href="#/forgot">{$_("auth.forgot")}</a>
      </div>

      {#if error}<div class="err">{error}</div>{/if}

      <button type="submit" class="btn btn--primary btn--block" disabled={loading}>
        {loading ? $_("auth.signingIn") : $_("auth.signIn")}
      </button>

      <p class="bottom">{$_("auth.newToOe")} <a href="#/request-access">{$_("auth.requestAccess")}</a></p>
      <p class="bottom"><a href="#/discover">{$_("auth.discoverLink")}</a></p>
    </form>
    </div>
  </section>
</div>

<style>
  .page {
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(0, 11fr) minmax(0, 9fr);
    background: var(--color-bg);
  }
  /* The hero scrolls (eight blocks + advantages); the form stays put beside it. */
  .form { position: sticky; top: 0; align-self: start; min-height: 100vh; }
  @media (max-width: 900px) {
    .page { grid-template-columns: 1fr; }
    /* Phones: the form first, the story below it. */
    .form { order: 0; position: static; min-height: 0; }
    :global(.page > .hero) { order: 1; }
  }


  .form { display: flex; align-items: center; justify-content: center; padding: 32px; }
  .form-inner { width: 100%; max-width: 420px; }
  .lang-row { display: flex; justify-content: flex-end; align-items: center; margin-bottom: 12px; }
  /* Phones: the hero (and its logo) sits below the form, so the brand goes
     here, at the top-left of the light column. Desktop has it in the hero. */
  .mobile-brand { display: none; margin-right: auto; }
  .mobile-brand img { height: 36px; width: auto; display: block; }
  @media (max-width: 900px) { .mobile-brand { display: block; } }
  .login-card {
    width: 100%; max-width: 420px;
    padding: 32px;
    display: flex; flex-direction: column; gap: 18px;
  }
  .login-card h2 { margin: 0; font-size: 24px; color: var(--color-surface-darker); text-align: center; }
  .login-card .muted { text-align: center; margin: 0; }
  .checkbox { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; }

  /* Password field with the reveal toggle sitting inside the input. */
  .pw-wrap { position: relative; display: flex; }
  .pw-wrap input { width: 100%; padding-right: 42px; }
  .pw-toggle {
    position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; padding: 0;
    border: none; background: none; cursor: pointer; border-radius: 50%;
    color: var(--color-text-muted);
  }
  .pw-toggle:hover { color: var(--color-primary); background: rgba(33,133,213,0.08); }
  .pw-toggle .material-icons-outlined { font-size: 20px; }
  .bottom { text-align: center; margin: 4px 0 0; font-size: 13px; color: var(--color-text-muted); }
  .err {
    background: var(--color-danger-soft);
    border: 1px solid var(--color-danger);
    color: var(--color-danger);
    padding: 8px 12px; border-radius: var(--radius); font-size: 13px;
  }
  .notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: rgba(33, 133, 213, 0.10);
    border: 1px solid rgba(33, 133, 213, 0.28);
    color: var(--color-surface-darker);
    padding: 10px 12px;
    border-radius: var(--radius);
    font-size: 13px;
    line-height: 1.35;
  }
  .notice .material-icons-outlined {
    color: var(--color-primary);
    font-size: 18px;
    line-height: 1;
    margin-top: 1px;
  }
</style>
